import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Collapse,
  IconButton,
  InputAdornment,
  TextField,
  Alert,
  Box,
  useTheme,
} from '@mui/material';
import { ErrorOutline, Visibility, VisibilityOff } from '@mui/icons-material';
import { LoadingButton } from 'components';
import useValidationSchema from './services/useValidationSchema';
import useNewPasswordRequest from './services/useNewPasswordRequest';

const NewPasswordForm = ({
  onSuccess,
  token,
}: {
  onSuccess: () => void;
  token: string;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const passwordSchema = useValidationSchema();
  const { response, error, loading, newPasswordRequest } =
    useNewPasswordRequest();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const { register, handleSubmit, formState, setValue, watch } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(passwordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });
  const { errors, isValid } = formState;

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  useEffect(() => {
    if (response) {
      onSuccess();
    }
  }, [response, onSuccess]);

  // @ts-ignore
  const onSubmit = (data) => newPasswordRequest(data, token);

  const inputStyle = useMemo(
    () => ({
      marginBottom: theme.spacing(3),
      width: '100%',
      '& input:-webkit-autofill': {
        '-webkit-background-clip': 'text',
        boxShadow: `0 0 0 100px ${theme.palette.grey[900]} inset`,
        '-webkit-box-shadow': `0 0 0 100px ${theme.palette.grey[900]} inset`,
      },
      '& input:-moz-autofill': {
        boxShadow: `0 0 0 100px ${theme.palette.grey[900]} inset`,
        '-moz-box-shadow': `0 0 0 100px ${theme.palette.grey[900]} inset`,
      },
      '& input:-ms-autofill': {
        boxShadow: `0 0 0 100px ${theme.palette.grey[900]} inset`,
        '-ms-box-shadow': `0 0 0 100px ${theme.palette.grey[900]} inset`,
      },
    }),
    [theme]
  );

  return (
    <Box
      component="form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        maxWidth: theme.spacing(48.75),
      }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Collapse in={!!error}>
        <Alert
          variant="outlined"
          severity="error"
          color="error"
          sx={{
            marginBottom: theme.spacing(4),
            [theme.breakpoints.down('sm')]: {
              color: theme.palette.grey[50],
              fontSize: 12,
            },
          }}
          data-testid="newPasswordErrorAlert"
          icon={<ErrorOutline color="error" />}
        >
          {error && error.message}
        </Alert>
      </Collapse>
      <TextField
        sx={inputStyle}
        fullWidth
        {...register('password')}
        type={isPasswordVisible ? 'text' : 'password'}
        error={!!errors.password}
        helperText={errors.password && errors.password.message}
        label={t('fields:newPassword')}
        variant="outlined"
        placeholder={t('auth:newPasswordPlaceholder')}
        id="password"
        onChange={(event) => {
          setValue('password', event.target.value);
        }}
        InputProps={{
          endAdornment:
            watch('password').length > 0 ? (
              <InputAdornment position="end" data-testid="show-new-password">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={togglePasswordVisibility}
                  edge="end"
                  size="large"
                >
                  {isPasswordVisible ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ) : null,
        }}
      />

      <TextField
        sx={inputStyle}
        fullWidth
        {...register('confirmPassword')}
        type={isPasswordVisible ? 'text' : 'password'}
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword && errors.confirmPassword.message}
        label={t('fields:confirmPassword')}
        variant="outlined"
        placeholder={t('auth:confirmPasswordPlaceholder')}
        id="confirmPassword"
        InputProps={{
          endAdornment:
            watch('password').length > 0 ? (
              <InputAdornment position="end">
                <IconButton
                  onClick={togglePasswordVisibility}
                  edge="end"
                  data-testid="show-confirm-password"
                  size="large"
                >
                  {isPasswordVisible ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ) : null,
        }}
      />
      <LoadingButton
        type="submit"
        variant="rounded"
        fullWidth
        sx={{
          marginBottom: theme.spacing(5),
          height: theme.spacing(5),
        }}
        disabled={!isValid}
        loading={loading}
        data-testid="submitButton-newPassword"
      >
        {t('auth:reset')}
      </LoadingButton>
    </Box>
  );
};

export default NewPasswordForm;
